import { useState } from "react";
import { Markdown } from "../markdown";
import type { ComponentProps } from "./index";

interface Tip {
	title: string;
	body: string;
}

function parseTips(args: Record<string, unknown>): Tip[] {
	const raw = args.tips;
	if (!Array.isArray(raw)) return [];
	return raw
		.map((t) => t as Partial<Tip>)
		.filter((t): t is Tip => typeof t?.title === "string" && typeof t?.body === "string" && t.body.trim() !== "");
}

/**
 * The aside the reply is not allowed to carry, drawn shut.
 *
 * The house style forbids a "by the way" sidebar in the answer, and the `add_tips` tool is where
 * that content goes instead. So this box is closed on arrival and stays closed until the reader
 * asks for it — an aside that opened itself would be the sidebar again, just with a border. The
 * titles on the collapsed row are what let the reader decide without opening it, which is why the
 * tool requires one per tip.
 *
 * Unlike the other display renderers this one draws its arguments directly rather than fetching
 * what they point at: a tip is the model's own prose and has no computed source it could drift
 * from. See `TIPS_SPEC` in `src/profile/components.ts`.
 */
export function Tips({ args }: ComponentProps): React.ReactNode {
	const [open, setOpen] = useState(false);
	const tips = parseTips(args);

	// A call with nothing usable in it draws nothing at all. The alternative is an empty box the
	// reader opens for no reason, which is worse than the aside never having been offered.
	if (tips.length === 0) return null;

	return (
		<div className={`collapsible tips${open ? " open" : ""}`}>
			<button className="collapsible-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
				<span className="caret">{open ? "▾" : "▸"}</span>
				<span className="tool-name">补充 {tips.length} 条</span>
				{!open && <span className="tips-titles">{tips.map((t) => t.title).join(" · ")}</span>}
			</button>
			{open && (
				<div className="collapsible-body">
					{tips.map((tip, i) => (
						<div className="tip" key={i}>
							<div className="tip-title">{tip.title}</div>
							<Markdown text={tip.body} />
						</div>
					))}
				</div>
			)}
		</div>
	);
}
